/** @ken/module-expenses — frontend entry point.
 *
 * 0.2.0 — story-page adoption (ADR-0081). The in-list Sheet is retired;
 * expenses/$id now carries a full story-page shell with Overview / Line Items
 * / Related / Notes routed tabs. The ExpenseDetailSheet export is kept for
 * backwards compatibility but is no longer used by the list page.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { isRecordId, RecordNotFound } from "@ken/ui";
import { createRoute, notFound, redirect } from "@tanstack/react-router";

import type { ExpenseStorySearch } from "./routes/story/expense-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadExpenseStoryTabs = () => import("./routes/story/expense-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const ExpenseListPage = lazyScreen(
  () => import("./routes/expense-list"),
  "ExpenseListPage",
);
const ExpenseStoryDetail = lazyScreen(
  () => import("./routes/story/expense-story-detail"),
  "ExpenseStoryDetail",
);
const ExpenseActivityRoute = lazyScreen(
  loadExpenseStoryTabs,
  "ExpenseActivityRoute",
);
const ExpenseLineItemsRoute = lazyScreen(
  loadExpenseStoryTabs,
  "ExpenseLineItemsRoute",
);
const ExpenseNotesRoute = lazyScreen(
  loadExpenseStoryTabs,
  "ExpenseNotesRoute",
);
const ExpenseOverviewRoute = lazyScreen(
  loadExpenseStoryTabs,
  "ExpenseOverviewRoute",
);
const ExpenseRelatedRoute = lazyScreen(
  loadExpenseStoryTabs,
  "ExpenseRelatedRoute",
);
// Kept for backwards compatibility — no longer used by the list page.
export { ExpenseDetailSheet } from "./components/expense-detail-sheet";

// UI-06: expenses is the clearest case for the item's "create surface out of
// scope" outcome — there is no create endpoint for an expense at all (the API
// exposes no POST /api/modules/expenses), so a form here could not save if it
// were built. Expenses are captured in the source system and imported. Like
// fixed assets, this module fired TWO 422s on a literal `new`; the guard below
// stops both.

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "expenses",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3). validateSearch keeps
    // them typed + reload-proof; the manual writeQueryParams is retired.
    validateSearch: (raw: Record<string, unknown>): ExpenseListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: ExpenseListPage,
  });

  // UI-06: a STATIC route for the literal `new`, so the id route below can
  // never match it and fire a detail read for a record called "new".
  const literalNewRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "expenses/new",
    component: RecordNotFound,
  });

  // ── Story-page detail (ADR-0081): parent expenses/$id shell + routed
  //    tab children + an index redirect to …/overview. ─────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "expenses/$id",
    // UI-06's id-shape guard: stops both the detail read and the
    // graph/related read before either is issued.
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { id?: string }).id)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    // The list's filter/search + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): ExpenseStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: ExpenseStoryDetail,
  });

  // Index redirect expenses/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/expenses/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: ExpenseOverviewRoute,
  });
  const lineItemsRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "line-items",
    component: ExpenseLineItemsRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: ExpenseRelatedRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: ExpenseNotesRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: ExpenseActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      lineItemsRoute,
      relatedRoute,
      activityRoute,
      notesRoute,
    ],
  );

  // Static before parameterised.
  return [listRoute, literalNewRoute, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface ExpenseListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
